/**
 * paintPulse — "a tela está produzindo FRAMES?" (BUG-197)
 *
 * ── O estado que isto detecta ───────────────────────────────────────────────
 * O diário do build 84 (01/09) provou que, nos episódios de "tela que não
 * carrega" na volta do background, o TRANSPORTE está vivo (`wake → verify-ok`
 * em ~90ms, ida e volta ao servidor) — quem morre é a PINTURA: o WKWebView
 * para de desenhar (tela creme = o fundo NATIVO onde nenhum tile foi pintado;
 * capas e seções somem ao rolar). E o relato de device fecha o quadro: **toque
 * e scroll NÃO curam** — só matar o app curava, até aqui.
 *
 * A sonda: dois `requestAnimationFrame` ANINHADOS contra um teto. rAF só
 * dispara quando o motor produz um update de renderização; o primeiro pode
 * estar pegando carona num frame já agendado, então é o SEGUNDO — que exige um
 * frame novo — que prova pulso. Sem pulso no teto, a tela está congelada e a
 * saída é a mesma da fila morta: reload com vaga (é literalmente o que "fechar
 * e abrir o app" faz, automático).
 *
 * ⚠️ Aba OCULTA não tem rAF por definição — o chamador só pode agir sobre
 * `frozen` se a página estiver visível NO DESFECHO (o `isVisible` é relido no
 * timeout, como o `isOnline` da verificação de entrega): retomar e esconder de
 * novo no meio da janela não pode virar reload.
 */

export type PaintPulseVerdict = "painting" | "frozen" | "hidden";

/**
 * Nudge de COMPOSITOR pelo JS (BUG-197, 4ª fatia).
 *
 * O vídeo do build 87 mostrou a forma exata do bug: tela CREME inteira ao
 * voltar do background, com JS, rAF e Firestore vivos. É a assinatura de um
 * mecanismo do WebKit iOS — ao retomar, o WKWebView ESCONDE o conteúdo até o
 * próximo commit da árvore de layers do processo web; se esse commit não
 * chega, fica o `backgroundColor` nativo para sempre. Um commit só acontece
 * quando a árvore de layers MUDA — rAF sem mudança de camada não conta.
 *
 * Isto força a mudança: `opacity` < 1 no `<html>` cria uma camada composta
 * (e um stacking context) SEM virar containing block de `fixed` — a
 * armadilha do `transform` (classe BUG-98). Um frame depois, tira. Dois
 * commits, imperceptível a 0,999, custo zero em retomada saudável.
 */
export function nudgeCompositor(
  raf: Raf = defaultRaf,
  root: HTMLElement | null = typeof document === "undefined" ? null : document.documentElement,
): void {
  if (!root) return;
  const previous = root.style.opacity;
  root.style.opacity = "0.999";
  raf(() => {
    root.style.opacity = previous;
  });
}

/**
 * REPAINT COMPLETO pelo JS (BUG-197, 5ª fatia).
 *
 * O nudge acima muda a ESTRUTURA da árvore de layers (dois commits), mas não
 * obriga nenhuma layer a REPINTAR o conteúdo dela. Há um mecanismo do WebKit
 * iOS que o nudge não alcança: ao entrar em background o processo web marca
 * os backing stores das layers como VOLÁTEIS (`WebPage::markLayersVolatile`,
 * medido na fonte), e o sistema pode purgá-los sob pressão de memória. Uma
 * layer cujo buffer foi purgado e que NÃO for repintada volta à tela vazia —
 * é a forma de "capas e seções somem" que só o toque (que muda o estilo do
 * elemento tocado e o repinta) devolve. Rebuild de estrutura não repinta.
 *
 * O truque: `visibility: hidden` no `<html>` e de volta, com um flush de
 * estilo FORÇADO entre os dois. `visibility` é herdada e muda o estilo de
 * TODO elemento — cada `styleDidChange` marca o repaint da layer dele —, e
 * como os dois estados são aplicados na mesma tarefa, o próximo commit sai
 * já com o estilo restaurado: toda layer repinta, nenhum frame em branco,
 * ZERO layout (visibility não é diff de layout). Nunca `display: none`, que
 * destruiria a árvore de render e perderia scroll, foco e lazy-images.
 *
 * `flush` injetável: em produção é `getComputedStyle(root).visibility`, que
 * é o que obriga o motor a resolver o estilo AGORA (sem ele as duas escritas
 * colapsariam numa só e nada seria invalidado).
 */
export function forceFullRepaint(
  root: HTMLElement | null = typeof document === "undefined" ? null : document.documentElement,
  flush: (el: HTMLElement) => void = (el) => void getComputedStyle(el).visibility,
): void {
  if (!root) return;
  const previous = root.style.visibility;
  root.style.visibility = "hidden";
  flush(root);
  root.style.visibility = previous;
  flush(root);
}

/**
 * Teto do pulso. Um frame chega em ~16ms numa tela sã; o folgado cobre o
 * WebKit acordando devagar depois do background sem confundir lentidão com
 * congelamento — um falso `frozen` custa um reload.
 */
export const PAINT_PULSE_TIMEOUT_MS = 700;

type Raf = (cb: () => void) => void;

const defaultRaf: Raf = (cb) => {
  requestAnimationFrame(() => cb());
};

/**
 * Corre dois rAFs aninhados contra o relógio. `raf` e `isVisible` injetados
 * para o teste não depender do relógio de frames do jsdom (que não existe).
 */
export async function probePaintPulse(
  raf: Raf = defaultRaf,
  isVisible: () => boolean = () => document.visibilityState === "visible",
  timeoutMs: number = PAINT_PULSE_TIMEOUT_MS,
): Promise<PaintPulseVerdict> {
  return new Promise<PaintPulseVerdict>((resolve) => {
    let settled = false;

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      // A visibilidade é relida NO DESFECHO (não capturada no início): rAF
      // parado em aba oculta é o comportamento normal do navegador — retomar
      // e esconder de novo no meio da janela não pode virar reload.
      resolve(isVisible() ? "frozen" : "hidden");
    }, timeoutMs);

    raf(() => {
      raf(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve("painting");
      });
    });
  });
}
